import React, {Component, ReactNode, RefObject} from 'react'
import styled from 'styled-components'
import {Box, IconButton, OutlinedInput} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {
    FlexContainer,
    FullWidthLeftRowFlexContainer,
    FullWidthRowFlexContainer,
    LeftRowFlexContainer,
    RightRowFlexContainer,
    RowFlexContainer
} from '../../../../../styledComponents/FlexComponents'
import {HKConstants} from '../../../../../HKConstants'
import {PlainStyledButton, UnstyledLinkStyledButton} from '../../../../../styledComponents/Button'
import {BlockSpacer} from '../../../../../styledComponents/ResponsiveConvenience'
import {Colors, FontSize} from '../../../../../styledComponents/Constants'
import {PolicyCheckBoxStyled} from '../../../applications/common/resident/NatResidentApplicationsCommon'
import NatDisasterCommentError, {InputError} from './NatDisasterCommentError'
import {ImgAddIcon} from '../../../../common/icons/NatSvgIcon'

export interface DisasterCommentFile {
    fileNumber: number | null
    fileName: string
    fileData: string | null
    fileSize: number
}

interface Props {
    id: string
    submitButtonLabel: string
    clearButtonLabel: string
    onClickSubmitButton: () => Promise<void>
    onClickCancelButton?: () => void
    onChange: (
        comment: string,
        posterNamePrivateFlag: boolean,
        commentFileList: DisasterCommentFile[],
        deleteFileNumberList: number[],
        inputError?: InputError
    ) => void
    comment: string
    posterNamePrivateFlag: boolean
    placeholder?: string
    commentFileList: DisasterCommentFile[]
    deleteFileNumberList: number[]
    inputError?: InputError
}

class NatDisasterCommentInputForm extends Component<Props> {
    commentFileRef: RefObject<HTMLInputElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.commentFileRef = React.createRef()
        this.handleChangeComment = this.handleChangeComment.bind(this)
        this.handleCheckNamePrivate = this.handleCheckNamePrivate.bind(this)
        this.handleClickAddFileButton = this.handleClickAddFileButton.bind(this)
        this.handleChangeFileInput = this.handleChangeFileInput.bind(this)
        this.handleClickDeleteFileButton = this.handleClickDeleteFileButton.bind(this)
        this.focusTextArea = this.focusTextArea.bind(this)
    }

    handleChangeComment(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.props.onChange(
            event.target.value,
            this.props.posterNamePrivateFlag,
            this.props.commentFileList,
            this.props.deleteFileNumberList
        )
    }

    focusTextArea(): void {
        const fileUpload = document.getElementById('commentInputArea-'+ this.props.id) as HTMLInputElement
        if (fileUpload) {
            fileUpload.focus();
        }
    }

    handleCheckNamePrivate(event: React.ChangeEvent<HTMLInputElement>): void {
        this.props.onChange(
            this.props.comment,
            event.target.checked,
            this.props.commentFileList,
            this.props.deleteFileNumberList
        )
    }

    handleClickAddFileButton(): void {
        const fileUpload = document.getElementById('fileUpload-' + this.props.id) as HTMLInputElement
        if (fileUpload) {
            this.props.onChange(
                this.props.comment,
                this.props.posterNamePrivateFlag,
                this.props.commentFileList,
                this.props.deleteFileNumberList
            )
            fileUpload.click()
        }
    }

    handleChangeFileInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files === null ||
            event.target.files.length === 0 ||
            event.target.files[0] === null) {
            return
        }

        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {commentFileList} = this.props
            const fileName = event.target.files[0].name
            const fileSize = event.target.files[0].size
            const extension = fileName.split('.').pop()?.toLowerCase()
            const allowedExtensions = ['jpg', 'jpeg', 'png']

            let inputError: InputError | undefined
            if (fileSize > HKConstants.CMN.DIS.MAX_FILE_SIZE * 1024 * 1024) {
                inputError = {
                    title: '画像を追加できませんでした。',
                    messages: ['アップロードの上限サイズ(20MB)を超えています。画像のサイズを確認してください。']
                }
            }

            if (!extension || !allowedExtensions.includes(extension)) {
                inputError = {
                    title: '画像を追加できませんでした。',
                    messages: ['jpeg,jpg,png,heif,heicファイルを選択してください。']
                }
            }

            if (inputError) {
                this.props.onChange(
                    this.props.comment,
                    this.props.posterNamePrivateFlag,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    inputError
                )
                if (this.commentFileRef.current) {
                    this.commentFileRef.current.value = ''
                }
                return
            }

            const reader: FileReader = new FileReader()
            reader.onload = () => {
                commentFileList.push({
                    fileNumber: null,
                    fileName: fileName,
                    fileSize: fileSize,
                    fileData: String(reader.result)
                })
                this.props.onChange(
                    this.props.comment,
                    this.props.posterNamePrivateFlag,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    this.props.inputError
                )
            }
            reader.onerror = () => {
                this.props.onChange(
                    this.props.comment,
                    this.props.posterNamePrivateFlag,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    {
                        title: '画像を追加できませんでした。',
                        messages: ['画像の読み込みに失敗しました。もう一度画像を選択してください。'],
                        fileNameList: [`${fileName}`]
                    }
                )
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.commentFileRef.current) {
            this.commentFileRef.current.value = ''
        }
    }

    handleClickDeleteFileButton(deleteIndex: number): void {
        const {commentFileList, deleteFileNumberList} = this.props
        const newCommentFileList: DisasterCommentFile[] = []
        const newDeleteFileNumberList: number[] = [...deleteFileNumberList]

        for (let index = 0; index < commentFileList.length; index++) {
            const commentFile = commentFileList[index]
            if (index !== deleteIndex) {
                newCommentFileList.push(commentFile)
            } else if (commentFile.fileNumber) {
                newDeleteFileNumberList.push(commentFile.fileNumber)
            }
        }

        this.props.onChange(
            this.props.comment,
            this.props.posterNamePrivateFlag,
            newCommentFileList,
            newDeleteFileNumberList,
            this.props.inputError
        )
    }

    renderCommentInputAreaScrollText(): ReactNode {
        const {
            comment,
            placeholder,
            commentFileList
        } = this.props

        return <CommentInputArea onClick={this.focusTextArea} isImageAttached={commentFileList.length > 0}>
            <StyledOutlinedInput
                inputProps={{
                    minLength: 0,
                    maxLength: 2000
                }}
                rows={5}
                value={comment}
                fullWidth
                multiline
                placeholder={placeholder}
                onChange={this.handleChangeComment}
                id={'commentInputArea-'+ this.props.id}
            />
            {commentFileList.length > 0 &&
                <Box display={'flex'} style={{gap: '5px'}} mt={'15px'}>
                    {commentFileList.map((commentFile, index) => {
                        return (
                            <ThumbnailContainer key={index}>
                                <ThumbnailImg src={String(commentFile.fileData)}/>
                                <VisibilityIconButton onClick={() => this.handleClickDeleteFileButton(index)}>
                                    <StyledNotInterestedIcon src={`/resources/images/icons/cancel.svg`} alt=""/>
                                </VisibilityIconButton>
                            </ThumbnailContainer>
                        )
                    })}
                </Box>
            }
        </CommentInputArea>
    }

    render(): ReactNode {
        const {
            comment,
            commentFileList,
            submitButtonLabel,
            onClickSubmitButton,
            onClickCancelButton,
            inputError
        } = this.props
        const disabledAddFileButton = commentFileList.length >= 5
        const isSubmitDisabled = comment.length === 0
        return <>
            <StyledFlexContainer>
                {inputError &&
                    <>
                        <NatDisasterCommentError inputError={inputError}/>
                        <Box height={'15px'}/>
                    </>
                }
                {this.renderCommentInputAreaScrollText()}
                <Box height={'5px'}/>
                <FullWidthRowFlexContainer>
                    <PolicyCheckBoxStyled>
                        <LeftRowFlexContainer>
                            <StyledCheckBox
                                type="checkbox"
                                checked={this.props.posterNamePrivateFlag}
                                onChange={this.handleCheckNamePrivate}
                            />
                            <BlockSpacer width={'5px'}/>
                            <Box color={Colors.Body}>
                                名字を非公開にする
                            </Box>
                        </LeftRowFlexContainer>
                    </PolicyCheckBoxStyled>
                </FullWidthRowFlexContainer>
                <Box height={'5px'}/>
                <FullWidthLeftRowFlexContainer>
                    <LeftRowFlexContainer>
                        <AddFileButton
                            id={'addFileButton'}
                            onClick={this.handleClickAddFileButton}
                            disabled={disabledAddFileButton}
                        >
                            <LeftRowFlexContainer>
                                <ImgAddIcon
                                    color={disabledAddFileButton ? Colors.AccentLight : Colors.AccentDark}
                                />
                                <BlockSpacer width={'5px'}/>
                                <Box
                                    color={disabledAddFileButton ? Colors.AccentLight : Colors.Body}>画像を添付する</Box>
                            </LeftRowFlexContainer>
                        </AddFileButton>
                        <DisplayNoneFileInput
                            type={'file'}
                            id={'fileUpload-' + this.props.id}
                            ref={this.commentFileRef}
                            onChange={this.handleChangeFileInput}
                        />
                    </LeftRowFlexContainer>
                </FullWidthLeftRowFlexContainer>
                <RightRowFlexContainer>
                    <RowFlexContainer>
                        {
                            onClickCancelButton &&
                            <Box mr={'20px'}>
                                <UnstyledLinkStyledButton onClick={onClickCancelButton}>
                                    {this.props.clearButtonLabel}
                                </UnstyledLinkStyledButton>
                            </Box>
                        }
                        <SubmitPrimaryButton
                            onClick={onClickSubmitButton}
                            disabled={isSubmitDisabled}
                        >
                            {submitButtonLabel}
                        </SubmitPrimaryButton>
                    </RowFlexContainer>
                </RightRowFlexContainer>
            </StyledFlexContainer>
        </>
    }
}

export default NatDisasterCommentInputForm

const StyledCheckBox = styled.input`
    width: 18px;
    height: 18px;
`

const DisplayNoneFileInput = styled.input`
    display: none;
`

const ThumbnailContainer = styled(Box)`
    display: flex;
    position: relative;
    flex-wrap: wrap;
    width: calc((100% - 20px) / 5);
    max-width: 60px;
    height: 60px;
`

const ThumbnailBox = styled(Box)`
    display: flex;
    position: relative;
    flex-wrap: wrap;
    width: 100%;
`

const ThumbnailImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: ${Colors.AccentLight};
`

const VisibilityIconButton = withStyles({
    root: {
        padding: 0,
        color: '#00000066',
        position: 'absolute',
        left: '46px',
        top: '2px',
        '& path': {
            transform: 'scale(1.2)',
            transformOrigin: 'center'
        }
    }
})(IconButton)

const StyledNotInterestedIcon = styled.img`
    width: 12px;
    height: 12px;
`

const StyledFlexContainer = styled(FlexContainer)`
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
`

const StyledOutlinedInput = withStyles({
    root: {
        fontSize: FontSize.Body,
        color: Colors.Body,
        padding: 0,
        alignItems: 'flex-start',
        flex: 1
    },
    input: {
        fontFamily: '"Noto Sans JP", sans-serif',
        lineHeight: 1.5
    },
    notchedOutline: {
        border: 'none'
    }
})
(OutlinedInput)

const CommentInputArea = styled.div<{ isImageAttached: boolean }>`
    display: flex;
    flex-direction: column;
    background-color: ${Colors.Surface};
    border: ${Colors.AccentLight} 1px solid;
    border-radius: 4px;
    padding: 15px;
    box-sizing: border-box;
    width: 100%;
    height: ${props => props.isImageAttached  ? '224px' : '150px'};
`

const AddFileButton = styled(UnstyledLinkStyledButton)`
    color: ${Colors.AccentDark};

    :disabled {
        cursor: default;
        color: ${Colors.AccentLight};
    }
`

const SubmitPrimaryButton = styled(PlainStyledButton)`
    color: ${Colors.Surface};
    background-color: ${Colors.Primary};

    border: none;
    border-radius: 4px;
    line-height: 1.5;
    padding: 8px 15px;

    :active {
        background-color: ${Colors.PrimaryActive};
        box-shadow: none;
    }

    :disabled {
        pointer-events: none;
        border: none;
        background-color: ${Colors.AccentLight};
        box-shadow: none;
    }
`